import { useState, useEffect } from "react";
import {
  Typography,
  LinearProgress,
  CircularProgress,
  TableRow,
  TableCell,
  Grid,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  useUserContext,
  useUserSchools,
  useSchoolContext,
} from "../../../contexts/ProviderProvider";
import useNotification from "../../../hooks/useNotification";
import MUIDataTable from "mui-datatables";
import TrueFalseCheckbox from "../../common/TrueFalseCheckbox";
import MultiSelect from "../../common/MultiSelect";
import DateRangePicker from "../../common/DateRangePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import sample_data from "./sample_data.json";
import { getFinancialFlattened, getFinancialOrderTransaction, getFinancialTotals } from "../../../API/compliance";
import pako from 'pako';
import { CloudDownload } from "@mui/icons-material";

dayjs.extend(utc);
dayjs.extend(timezone);

const format2 = (v) => Number(v).toFixed(2);

const ExpandableRow = (props) => {
  console.log('props', props);
  const rows = props.data.sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });

  const columns = [
    {
      label: "Title",
      name: "title",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div
            style={{
              whiteSpace: "nowrap",
            }}
          >
            {value.replace(props.school, "").replace(/-[^-]*-/, "-")}
          </div>
        ),
      },
    },
    { label: "Price", name: "price", options: {} },
    { label: "Quantity", name: "quantity", options: {} },
    {
      label: "Date",
      name: "date",
      options: {
        customBodyRender: (value, tableMeta, updateValue) =>
          new Date(value).toLocaleDateString(),
      },
    },
    {
      name: "royaltyBearing",
      label: "Royalty Bearing",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <TrueFalseCheckbox
            checked={value !== "" && value !== null && !value}
            disabled
          />
        ),
      },
    },
    {
      name: "type",
      label: "Type",
      options: {},
    },
    {
      name: "description",
      label: "Description",
      options: {
        customBodyRender: (value) => {
          return (
            <div
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
              }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          );
        },

        setCellProps: () => ({
          style: {
            minWidth: "700px",
            maxWidth: "900px",
          },
        }),
      },
    },
  ];

  const options = {
    filter: true,
    sortOrder: { name: "date", direction: "desc" },
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: 10,
  };

  return (
    <div
      style={{
        margin: "10px 10px",
      }}
    >
      <MUIDataTable
        title={"Order History"}
        data={rows}
        columns={columns}
        options={options}
        components={{
          TableToolbar(props) {
            return null;
          },
        }}
      />
    </div>
  );
};

const BusinessTotalsDashboard = ({ financeUniversityData }) => {

  const titles = {
    gross_profit: "Net Profit",
    player_profit: "Athlete Profit",
    quantity_sum: "Total Quantity",
    sales_sum: "Total Sales",
    total_order: "Total Orders",
    total_royalties: "Total Royalty",
    school_ytd: "School YTD Rank"
  }

  return (
    <Grid container spacing={1}>
      {Object.entries(financeUniversityData).map(([key, value]) => {
        if (!value || value === "") return;
        return (
          <Grid item xs={12} sm={4} md={4} lg={2} key={key}>
            <Card>
              <CardContent>
                <Typography
                  variant="h7"
                  gutterBottom
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {titles[key] ? titles[key] : key}
                </Typography>
                <Typography variant="h5">{titles[key] && key === "school_ytd" ? value : format2(value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )
      })}
    </Grid>
  );
};

const ValueInfo = () => {

  const infoData = {
    "Historical Price": "Price charged for a single product at time of purchase",
    "Historical Cost": "Cost of a single product at time of purchase",
    "Quantity Sold": "Total number of items purchased",
    "Total Orders": "Total number of orders placed for each athlete",
    "Checkout Price": "final amount paid for an order, Calculated as (Historical Price × Quantity) - Total Discounts - Total Rewarded Gift Card",
    "Net Revenue": "Sum of all Checkout Prices across orders",
    "Cost of Goods": "Sum of all historical costs for items sold",
    "Transaction Fee": "Sum of transaction fees, calculated per item as (Transaction Fee × Historical Price × Quantity) ÷ Order Total",
    "University Royalty Fee": "Sum of university royalty fees, calculated as University Royalty Rate × Checkout Price",
    "Service Fee": "Sum of service fees, calculated as 0.13 × Checkout Price",
    "Transfer Fee": "Sum of transfer fees, calculated as 0.02 × Checkout Price",
    "Total Fee": "Sum of Service Fee, University Royalty Fee, Transfer Fee, Transaction Fee, and Cost of Goods",
    "Net Profit": "Net Revenue - Total Fee",
    "Athlete Profit": "Gross Profit × Athlete Merchandise Royalty Rate",
  }

  const [expanded, setExpanded] = useState(false);

  const handleChange = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      style={{
        margin: "10px",
        flex: '0 1 auto',
      }}
    >
      <AccordionSummary
        expandIcon={true && <ArrowDownwardIcon />}
        aria-controls="TransferPortal-content"
        id="TransferPortal-header"
      >
        <Typography>Calculation Info</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {Object.entries(infoData).map(([key, value]) => {
            return (
              <Grid item xs={6} key={key}>
                <Card sx={{ bgcolor: "info.contrastText" }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                    >
                      {key}
                    </Typography>
                    <Typography
                      variant="h7"
                      gutterBottom
                    >
                      {value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const ComplianceFinancialDashboardV2 = (props) => {
  const [loadingData, setLoadingData] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [financeUniversityData, setFinanceUniversityData] = useState({});
  const [financeAthleteData, setFinanceAthleteData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [schoolInput, setSchoolInput] = useState("");
  const [includeSubRows, setIncludeSubRows] = useState(false);

  const today = new Date();
  const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth());

  const [startDate, setStartDate] = useState(
    dayjs
      .tz(startOfCurrentMonth.toISOString().split("T")[0], "America/Chicago")
      .startOf("day")
  );
  const [endDate, setEndDate] = useState(
    dayjs.tz(today.toISOString().split("T")[0], "America/Chicago").endOf("day")
  );

  const userSchools = useUserSchools();
  const { schools } = useSchoolContext();
  const user = useUserContext();
  const notify = useNotification();

  const TIMEOUT_DURATION = 10000;

  useEffect(() => {
    let timeoutId;

    if (!user || !userSchools) {
      timeoutId = setTimeout(() => {
        window.location.reload(); // Refresh the page
      }, TIMEOUT_DURATION);
      return () => clearTimeout(timeoutId);
    }

    setSelectedSchools([userSchools[0]]);

    clearTimeout(timeoutId);
  }, [user, userSchools]);

  useEffect(() => {
    if (!selectedSchools || selectedSchools?.length < 1) {
      return;
    }
    setLoadingData(true);
    console.log('selectedSchools', selectedSchools)

    const fetch = async () => {
      try {
        setFinanceAthleteData([])
        const formatStartDate = startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss");
        const formatEndDate = endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss");

        const [oTByUniversity, oTByAthlete] = await Promise.all([
          getFinancialTotals(user, selectedSchools, formatStartDate, formatEndDate, props.view),
          getFinancialOrderTransaction(user, selectedSchools, formatStartDate, formatEndDate, props.view)
        ]);

        console.log('response from backend for Finance - v2: ', oTByUniversity, oTByAthlete)

        setFinanceUniversityData(oTByUniversity.data.obj[0]);
        setFinanceAthleteData(oTByAthlete.data.obj);
      } catch (error) {
        notify("Failed to get Finance data");
        console.error("Error fetching data: ", error);
      } finally {
        setLoadingData(false);
      }
    }

    fetch();
  }, [user, selectedSchools, startDate, endDate, props.view]);

  const handleFinancialDownload = async () => {
    if (isDownloading) return;

    if (!selectedSchools || selectedSchools?.length < 1) {
      notify("No School selected");
      return;
    }

    if (selectedSchools[0] === "Influxer School Of Gains") {
      notify("Influxer School Of Gains not a valid school to download Records");
      return;
    }

    setIsDownloading(true);

    try {
      const formatStartDate = startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss");
      const formateEndDate = endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss");

      const response = await getFinancialFlattened(user, selectedSchools, formatStartDate, formateEndDate, props.view)

      console.log('response: ', response)

      const blob = response.data;

      let contentDisposition = response.headers['content-disposition']

      let filename;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1].replace(/["']/g, '');
        }
      }

      if (!filename) {
        const fileStartDate = dayjs.tz(startDate, "America/Chicago").format("MM/DD/YYYY")
        const fileEndDate = dayjs.tz(startDate, "America/Chicago").format("MM/DD/YYYY")

        filename = `Athlete_Finance_Data_${fileStartDate}_to_${fileEndDate}.csv`;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      notify("Failed to download Financial data");
      console.error(error)
    } finally {
      setIsDownloading(false)
    }
  }

  const CustomDownloadButton = ({ onClick, isDownloading }) => {
    return (
      <Tooltip title={isDownloading ? "Downloading..." : "Download data"}>
        <span>
          <IconButton
            disabled={isDownloading}
            onClick={onClick}
            style={{ color: isDownloading ? 'gray' : 'inherit' }}
          >
            {isDownloading ? <CircularProgress size={24} /> : <CloudDownload style={{ fill: "text.primary" }} />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const customToolbar = () => {
    return (
      <CustomDownloadButton
        onClick={handleFinancialDownload}
        isDownloading={isDownloading}
      />
    );
  };

  // Transaction Fees (0.85%)	University Royalty Fee	Service Fee (4%)	Transfer Fee (2%)	Gross profit 	Player's profit (60%)
  const columns =
    props.view === "Internal"
      ? [
        {
          label: "Athlete",
          name: "name",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Shopify Collection ID",
          name: "shopify_collection_id",
          options: {
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "University",
          name: "university",
          options: {
            display:
              selectedSchools &&
              (selectedSchools?.length > 1 || selectedSchools[0] === "All"),
            sortThirdClickReset: true,
          },
        },
        { label: "Sport", name: "sport", options: { display: false } },
        { label: "State", name: "state", options: { display: false } },
        { label: "Division", name: "division", options: { display: false } },
        {
          label: "Conference",
          name: "conference",
          options: { display: false },
        },
        { label: "Gender", name: "gender", options: { display: false } },
        {
          label: "Sub Division",
          name: "subDivision",
          options: { display: false },
        },
        {
          label: "Quantity Sold",
          name: "quantity_sum",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {value}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Total Orders",
          name: "order_sum",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {value}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Net Revenue",
          name: "sales_sum",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Cost of Goods",
          name: "cogs",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Transaction Fees",
          name: "trans_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "University Royalty Fees",
          name: "roy_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Service Fees",
          name: "serv_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Transfer Fees",
          name: "transfer_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Net Profit",
          name: "net",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Athlete Profit",
          name: "play_prof",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
      ]
      : // External columns (one school)
      [
        {
          label: "Athlete",
          name: "name",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Quantity Sold",
          name: "quantity_sum",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {value}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Total Orders",
          name: "order_sum",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {value}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Net Revenue",
          name: "sales_sum",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Cost of Goods",
          name: "cogs",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Transaction Fees",
          name: "trans_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "University Royalty Fees",
          name: "roy_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Service Fees",
          name: "serv_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Transfer Fees",
          name: "transfer_fee",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Net Profit",
          name: "net",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          label: "Athlete Profit",
          name: "play_prof",
          options: {
            customBodyRender: (value, tableMeta, updateValue) => (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {format2(value)}
              </div>
            ),
            customHeadLabelRender: (columnMeta) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {columnMeta.label}
              </div>
            ),
            sortCompare: (order) => {
              return (a, b) => {
                const val1 = parseFloat(
                  a.data.replace("$", "").replace(",", "").trim()
                );
                const val2 = parseFloat(
                  b.data.replace("$", "").replace(",", "").trim()
                );

                if (isNaN(val1)) return -1;
                if (isNaN(val2)) return 1;

                return (val1 - val2) * (order === "asc" ? 1 : -1);
              };
            },
            filter: false,
            sortThirdClickReset: true,
          },
        },
      ];

  const options = {
    filter: true,
    // sortOrder: {name:"selected", direction:'desc'},
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: rowsPerPage,
    expandableRows: true,
    onChangeRowsPerPage: (num) => setRowsPerPage(num),
    renderExpandableRow: (rowData, rowMeta, a) => {
      const colSpan = 7;
      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <ExpandableRow
              user={user}
              data={financeAthleteData[rowMeta.dataIndex].player_trans}
              school={financeAthleteData[rowMeta.dataIndex].university}
              notify={notify}
            />
          </TableCell>
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: loadingData ? <CircularProgress /> : "No Records",
      },
    },
    download: false,
    customToolbar: customToolbar
  };

  return (
    <div style={{ width: "90%", paddingTop: "5vh" }}>
      {selectedSchools && (
        <Typography variant="h1">
          {selectedSchools?.length > 1
            ? selectedSchools.sort()[0] +
            ` and ${selectedSchools?.length - 1} more`
            : selectedSchools}
        </Typography>
      )}
      {loadingData && <LinearProgress />}
      {financeUniversityData && (
        <div
          style={{
            margin: "10px 10px",
          }}
        >
          <BusinessTotalsDashboard financeUniversityData={financeUniversityData} />
        </div>
      )}
      <ValueInfo />
      <div
        style={{
          margin: "10px 10px",
          // justifyContent: "center",
          width: "90%",
          // paddingLeft: "10px"
        }}
      >
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </div>
      {userSchools?.length > 1 && (
        <div
          style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
        >
          <MultiSelect
            value={selectedSchools}
            inputValue={schoolInput}
            options={schools
              ?.map((school) => school.schoolName)
              ?.filter((s) => !schoolInput.includes(s))}
            onChange={setSelectedSchools}
            onInputChange={setSchoolInput}
            label={"School List"}
            allButton
          />
        </div>
      )}
      {financeAthleteData &&
        <div
          style={{
            margin: "20px 10px",
          }}
        >
          <MUIDataTable
            title={"Finance Data"}
            data={financeAthleteData}
            columns={columns}
            options={options}
          />
        </div>
      }
    </div>
  );
};

export default ComplianceFinancialDashboardV2;